import { useMemo, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { Dropdown } from '@/components/cash-collections/components/Dropdown'
import type { Currency, DrillTarget, Lens, SalesReportProps, Settlement, TrendView } from '../types'
import { buildConsolidated } from './consolidate'
import { monthLabel } from './format'
import { SegmentedToggle } from './SegmentedToggle'
import { HeroCard } from './HeroCard'
import { StatPill } from './StatPill'
import { SettlementBridge } from './SettlementBridge'
import { TrendChart } from './TrendChart'
import { RevenueTable, resolveDrill } from './RevenueTable'
import { CreditNotesByDepartment, CreditNotesSection } from './CreditNotesSection'
import { DrilldownModal } from './DrilldownModal'

const TREND_MODES = ['Revenue', 'True Sales'] as const
type TrendMode = (typeof TREND_MODES)[number]
// The sub-view labels adapt to the mode: Revenue is invoiced (net of credit
// notes); True Sales is the order-month headline (net of PC credit).
const TREND_OPTS_BY_MODE: Record<TrendMode, { v: TrendView; l: string }[]> = {
  Revenue: [
    { v: 'afterCN', l: 'After CN' },
    { v: 'gross', l: 'Gross' },
    { v: 'priorYear', l: 'vs prior yr' },
  ],
  'True Sales': [
    { v: 'afterCN', l: 'True Sale' },
    { v: 'gross', l: 'Gross Sale' },
    { v: 'priorYear', l: 'vs prior yr' },
  ],
}

const RANGES = ['1M', '3M', '6M', '12M', 'YTD', 'All'] as const
type RangeKey = (typeof RANGES)[number]
// The headline measures that re-aggregate over the selected window (order basis).
const MEASURE_KEYS = [
  'grossSale', 'trueSales', 'netSales', 'revenue', 'pipeline',
  'cashCollected', 'outstanding', 'pcCredit', 'card', 'invoiceWeb', 'invoiceQb', 'refunds',
] as const

/** SONAR Sales Report — True Sale hero, the two-way decomposition pills, the
 *  settlement bridge, the invoiced trend, the lens-aware revenue table and the
 *  credit-notes section. All data via props; view state is internal. */
type View = Currency | 'Consolidated'

export function SalesReport({ report, onDrilldown, onExport }: SalesReportProps) {
  const [view, setView] = useState<View>('Consolidated')
  // Order month is the default: it is the sales view and the same basis as the
  // headline True Sale. Invoiced month (the finance view) is one click away.
  const [lens, setLens] = useState<Lens>('Order month')
  const [trendView, setTrendView] = useState<TrendView>('afterCN')
  const [trendMode, setTrendMode] = useState<TrendMode>('Revenue')
  const [selectedMonth, setSelectedMonth] = useState<string>(report.months[report.months.length - 1])
  const [range, setRange] = useState<RangeKey>('1M')
  const [drill, setDrill] = useState<DrillTarget | null>(null)
  // The Overview's department chips link here as /sales-report?department=<id>; open that department.
  const { url } = usePage()
  const initialDepartment = useMemo(() => {
    const q = url.split('?')[1]
    return q ? new URLSearchParams(q).get('department') : null
  }, [url])

  const views: View[] = [...report.currencies, 'Consolidated']
  const isConsolidated = view === 'Consolidated'
  // Consolidated figures are reported in €; USD is converted at each month's rate.
  const displayCurrency: Currency = isConsolidated ? 'EUR' : (view as Currency)
  const consolidated = useMemo(() => buildConsolidated(report), [report])
  const cur = isConsolidated ? consolidated : report.data[view as Currency]
  const recognition = lens === 'Invoiced month'

  // Headline block is lens-invariant — True Sale is one number per month (glossary:
  // both splits sum to the same True Sale). It's the canonical decomposition:
  // True Sale = ordered that month, Revenue (Inv) = recognised that month, etc.
  // The Recognition/Order toggle re-bases only the revenue table below.
  const monthIdx = Math.max(0, report.months.indexOf(selectedMonth))
  const headline = cur.byMonth.map((b) => ({ ...b.order, revenue: b.recognition.revenue }))

  // Date-range window: N months ending at the selected month. The headline block
  // (hero, pills, settlement bridge) re-aggregates over the window; 1M is the
  // single selected month; YTD runs from January of the selected month's year.
  // The revenue table + trend stay on their YTD / 17-month views.
  const ytdMonths = (() => {
    const yr = selectedMonth.slice(-2)
    let start = monthIdx
    while (start > 0 && report.months[start - 1].slice(-2) === yr) start--
    return monthIdx - start + 1
  })()
  const rangeMonths =
    range === 'All' ? monthIdx + 1 : range === 'YTD' ? ytdMonths : range === '1M' ? 1 : range === '3M' ? 3 : range === '6M' ? 6 : 12
  const winStart = Math.max(0, monthIdx - rangeMonths + 1)
  const winLen = monthIdx - winStart + 1
  const isSingle = winLen === 1

  const sumWindow = (start: number, end: number): Record<string, number> | null => {
    if (start < 0 || end >= headline.length || start > end) return null
    const acc: Record<string, number> = {}
    MEASURE_KEYS.forEach((k) => (acc[k] = 0))
    for (let i = start; i <= end; i++) MEASURE_KEYS.forEach((k) => (acc[k] += headline[i][k]))
    return acc
  }
  const m = sumWindow(winStart, monthIdx)! // the selected window — always valid
  const priorM = sumWindow(winStart - winLen, winStart - 1) // prior equal-length period
  const yoyM = sumWindow(winStart - 12, monthIdx - 12) // same period last year

  const periodLabel = isSingle
    ? monthLabel(selectedMonth)
    : `${monthLabel(report.months[winStart])} – ${monthLabel(selectedMonth)}`
  const prevLabel = isSingle ? 'prior month' : 'prior period'
  const yoyLabel =
    winStart - 12 >= 0
      ? isSingle
        ? monthLabel(report.months[monthIdx - 12])
        : `${monthLabel(report.months[winStart - 12])} – ${monthLabel(report.months[monthIdx - 12])}`
      : ''

  // Reversal credit notes deducted across the window (invoice axis), for the Revenue gross→net line.
  const winMonthCodes = report.months.slice(winStart, monthIdx + 1)
  const revenueCreditNotes = cur.creditNotes.byMonth
    .filter((b) => winMonthCodes.includes(b.month))
    .reduce((t, b) => t + b.invReversal, 0)

  const monthSettlement: Settlement = {
    grossSale: m.grossSale,
    pcCredit: m.pcCredit,
    trueSales: m.trueSales,
    card: m.card,
    invoiceWeb: m.invoiceWeb,
    invoiceQb: m.invoiceQb,
    cashCollected: m.cashCollected,
    outstanding: m.outstanding,
    refunds: m.refunds,
    netSales: m.netSales,
  }

  // Resolve the clicked department / category against the (per-view) tree. Memoised so
  // the modal's inner navigation isn't reset on every render.
  const drillCtx = useMemo(() => resolveDrill(cur.revenueByUnit, drill), [cur, drill])
  const drillIdx = drill ? cur.monthsInYtd.indexOf(drill.month) : -1

  const handleCell = (target: DrillTarget) => {
    setDrill(target)
    onDrilldown?.(target)
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales Report</h1>
          <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
            <span className="font-medium text-stone-700 dark:text-stone-200">{periodLabel}</span> ·{' '}
            <span className="font-medium">{isConsolidated ? 'Consolidated €' : view}</span> · YTD {report.ytdYear}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Dropdown
            label="Month"
            value={selectedMonth}
            options={[...report.months].reverse().map((mo) => ({ value: mo, label: monthLabel(mo) }))}
            onChange={setSelectedMonth}
          />
          <SegmentedToggle options={RANGES} value={range} onChange={setRange} size="sm" ariaLabel="Date range" />
          <span className="inline-flex items-center gap-1">
            <SegmentedToggle options={views} value={view} onChange={setView} ariaLabel="Currency" />
            <InfoPopover term="currencyToggle" />
          </span>
          <button
            onClick={() => onExport?.(displayCurrency)}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-600 hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Hero + pills */}
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <HeroCard
            trueSales={m.trueSales}
            prevMonth={priorM ? priorM.trueSales : null}
            prevYear={yoyM ? yoyM.trueSales : null}
            prevYearLabel={yoyLabel}
            netSales={m.netSales}
            currency={displayCurrency}
            month={periodLabel}
            prevLabel={prevLabel}
            fxRate={isConsolidated ? report.fxRates[selectedMonth] : undefined}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:col-span-7 lg:grid-cols-4">
          <StatPill label="Revenue (Inv)" value={m.revenue} prevMonth={priorM ? priorM.revenue : null} prevYear={yoyM ? yoyM.revenue : null} prevYearLabel={yoyLabel} prevLabel={prevLabel} currency={displayCurrency} term="revenue" creditNotes={revenueCreditNotes} />
          <StatPill label="Pipeline (Pipe)" value={m.pipeline} prevMonth={priorM ? priorM.pipeline : null} prevYear={yoyM ? yoyM.pipeline : null} prevYearLabel={yoyLabel} prevLabel={prevLabel} currency={displayCurrency} term="pipeline" />
          <StatPill label="Settled" value={m.cashCollected} prevMonth={priorM ? priorM.cashCollected : null} prevYear={yoyM ? yoyM.cashCollected : null} prevYearLabel={yoyLabel} prevLabel={prevLabel} currency={displayCurrency} term="cashCollected" />
          <StatPill label="Outstanding" value={m.outstanding} prevMonth={priorM ? priorM.outstanding : null} prevYear={yoyM ? yoyM.outstanding : null} prevYearLabel={yoyLabel} prevLabel={prevLabel} currency={displayCurrency} term="outstanding" />
        </div>
      </div>

      {/* Settlement + Trend */}
      <div className="grid gap-4 lg:grid-cols-2">
        <SettlementBridge
          settlement={monthSettlement}
          currency={displayCurrency}
          month={periodLabel}
          breakdown={cur.settlementByCategory?.[selectedMonth]}
          hierarchy={report.hierarchy}
        />
        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <div>
                <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">
                  {trendMode === 'Revenue' ? 'Revenue trend' : 'True Sales trend'}
                </h3>
                <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
                  {trendMode === 'Revenue' ? 'Invoiced (recognised), 17 months' : 'Ordered — True Sale, 17 months'}
                </p>
              </div>
              <SegmentedToggle
                options={TREND_MODES}
                value={trendMode}
                onChange={setTrendMode}
                size="sm"
                ariaLabel="Revenue or True Sales trend"
              />
            </div>
            <span className="inline-flex items-center gap-1">
              <div className="inline-flex items-center gap-0.5 rounded-full bg-stone-100 p-0.5 dark:bg-stone-800/70">
                {TREND_OPTS_BY_MODE[trendMode].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setTrendView(o.v)}
                    className={cn(
                      'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                      trendView === o.v
                        ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-900/5 dark:bg-stone-950 dark:text-white'
                        : 'text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-100',
                    )}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <InfoPopover term="afterCnVsGrossToggle" />
            </span>
          </div>
          <div className="mt-3">
            <TrendChart
              trend={
                trendMode === 'Revenue'
                  ? cur.trend
                  : cur.byMonth.map((b) => ({ month: b.month, gross: b.order.grossSale, afterCN: b.order.trueSales }))
              }
              view={trendView}
              currency={displayCurrency}
            />
          </div>
        </div>
      </div>

      {/* Revenue table */}
      <RevenueTable
        units={cur.revenueByUnit}
        initialExpandedDepartment={initialDepartment}
        monthsInYtd={cur.monthsInYtd}
        currency={displayCurrency}
        lens={lens}
        lenses={['Invoiced month', 'Order month'] as const}
        onLensChange={setLens}
        creditNotesByMonth={cur.monthsInYtd.map((mo) => cur.creditNotes.byMonth.find((b) => b.month === mo)?.invReversal ?? 0)}
        creditNotesTree={cur.creditNotes.byHierarchy}
        totals={cur.totals}
        year={report.ytdYear}
        onCellClick={handleCell}
      />

      {/* Credit notes: who gets them (ranked by business unit › department), then the monthly section */}
      <CreditNotesByDepartment tree={cur.creditNotes.byHierarchy} currency={displayCurrency} ytdYear={report.ytdYear} monthsInYtd={cur.monthsInYtd} />
      <CreditNotesSection creditNotes={cur.creditNotes} currency={displayCurrency} ytdYear={report.ytdYear} />

      {/* Drill-down */}
      <DrilldownModal
        open={!!drillCtx}
        onClose={() => setDrill(null)}
        context={drillCtx}
        month={drill?.month ?? ''}
        monthIndex={drillIdx}
        recognition={recognition}
        currency={displayCurrency}
        measureLabel={recognition ? 'Revenue (Inv)' : 'True Sale (Ord)'}
      />
    </div>
  )
}
